import {
  Button,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CenteredDialogContent } from '#/components/ui/centered-dialog-content';
import { useAppSettings } from '#/providers/app-settings-provider';
import {
  DOCUMENT_PATH_DESCRIPTION,
  isValidDocumentPath,
} from '#/utils/document-path';

export interface CreateSubpageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayPath: string;
}

export function CreateSubpageModal({
  open,
  onOpenChange,
  displayPath,
}: CreateSubpageModalProps) {
  const { documentPathRegex } = useAppSettings();
  const navigate = useNavigate();
  const [segment, setSegment] = useState('');

  useEffect(() => {
    if (open) setSegment('');
  }, [open]);

  const trimmedSegment = segment.trim();
  const fullPath = displayPath
    ? `${displayPath}/${trimmedSegment}`
    : trimmedSegment;
  const isPathValid =
    !!trimmedSegment && isValidDocumentPath(fullPath, documentPathRegex);

  const handleCreate = useCallback(() => {
    if (!trimmedSegment) return;
    const path = displayPath
      ? `${displayPath}/${trimmedSegment}`
      : trimmedSegment;
    onOpenChange(false);
    setSegment('');
    navigate(`/documents/${path}`);
  }, [trimmedSegment, displayPath, navigate, onOpenChange]);

  return (
    <DialogRoot open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>Create subpage</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Enter the next part of the path for a child of{' '}
              <Text as="span" fontWeight="semibold" color="fg">
                {displayPath || '/'}
              </Text>
              .
            </Text>
            <Input
              placeholder={
                displayPath
                  ? 'e.g. new-page or nested/page'
                  : 'e.g. my-doc or folder/doc'
              }
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleCreate();
                }
                if (e.key === 'Escape') {
                  onOpenChange(false);
                  setSegment('');
                }
              }}
              size="sm"
              autoFocus
              aria-invalid={!!trimmedSegment && !isPathValid}
              borderColor={
                trimmedSegment && !isPathValid ? 'red.500' : undefined
              }
            />
            {trimmedSegment && !isPathValid && (
              <Text fontSize="xs" color="red.500">
                {DOCUMENT_PATH_DESCRIPTION}
              </Text>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} justify="flex-end" width="100%">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onOpenChange(false);
                setSegment('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => {
                void handleCreate();
              }}
              disabled={!isPathValid}
            >
              Create
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
